import { chartMode, propertyRows, timeLabel } from './device-chart-data'

// 内部聚合时间必须包含日期和服务端时区；用户的显示格式可能只有 HH:mm。
export const aggregateWireFormat = "yyyy-MM-dd'T'HH:mm:ssZZ"
const hasValue = value => value !== undefined && value !== null && value !== ''
const numeric = value => hasValue(value) && typeof value !== 'boolean' && Number.isFinite(Number(value)) ? Number(value) : null

export function followsDeviceChart (parameter = {}) {
  return chartMode(parameter) === 2 || parameter.liveFollow === true
}

export function liveRange (query, parameter) {
  // 结束为 now 时继续向未来延长；固定日期和 now-1h 等截止表达式严格封闭。
  const open = parameter.rangeMode !== 'fixed' && (!parameter.to || String(parameter.to).trim() === 'now')
  return { from: query.range.from, to: open ? Infinity : query.range.to }
}

export function inLiveRange (timestamp, range) {
  return Number.isFinite(timestamp) && timestamp >= range.from && timestamp <= range.to
}

export function propertySample (payload, query) {
  if (!payload || payload.success === false || String(payload.deviceId) !== query.deviceId || !payload.properties ||
    !Object.prototype.hasOwnProperty.call(payload.properties, query.property)) return null
  const timestamp = Number(payload.timestamp)
  if (!hasValue(payload.timestamp) || !Number.isFinite(timestamp)) return null
  return { timestamp, value: payload.properties[query.property], property: query.property, messageId: payload.messageId }
}

// 平台整机 MeasurementValue 使用属性采样时间；与原始消息一样转为内部历史接续样本。
export function measurementPropertySample (response, query) {
  const payload = response && response.payload, row = payload && payload.value
  if (!row || row.property !== query.property || (row.deviceId && String(row.deviceId) !== query.deviceId) ||
    !Object.prototype.hasOwnProperty.call(row, 'value') || !hasValue(payload.timestamp)) return null
  const timestamp = Number(payload.timestamp)
  return Number.isFinite(timestamp) ? { timestamp, value: row.value, property: query.property } : null
}

export function bodyWithRows (body, rows) {
  if (Array.isArray(body)) return rows
  if (body.result && !Array.isArray(body.result)) return { ...body, result: { ...body.result, data: rows, total: rows.length } }
  return { ...body, result: rows }
}

function aliases (property) {
  let prefix = '__chart_'
  while ([prefix + 'sum', prefix + 'count'].includes(property)) prefix += '_'
  return { sum: prefix + 'sum', count: prefix + 'count' }
}

export function liveAggregationQuery (query) {
  const extra = aliases(query.property), columns = query.data.columns.slice()
  if (columns[0].agg === 'AVG') columns.push(
    { property: query.property, alias: extra.sum, agg: 'SUM' },
    { property: query.property, alias: extra.count, agg: 'COUNT' })
  return { ...query, data: { columns, query: { ...query.data.query, format: aggregateWireFormat } } }
}

// 只在普通、顺序到达的秒/分/小时桶内增量计算。迟到、修正、跨日历边界
// 或尚无服务端桶锚点时交回服务端局部校准，不猜时区、不平均两个平均值。
export function createLiveAggregation (query, parameter) {
  const range = liveRange(query, parameter), extra = aliases(query.property)
  const agg = query.data.columns[0].agg, interval = query.data.query.interval
  const match = interval.match(/^(\d+)(s|m|h)$/)
  const duration = match ? Number(match[1]) * ({ s: 1000, m: 60000, h: 3600000 }[match[2]]) : 0
  const calendar = interval.match(/^(\d+)(d|w|M|y)$/)
  const span = duration || Number(calendar[1]) * ({ d: 86400000, w: 7 * 86400000, M: 32 * 86400000, y: 366 * 86400000 }[calendar[2]])
  const buckets = new Map(), seen = new Map()
  let anchor, lastTimestamp = -Infinity
  const trim = () => {
    if (buckets.size > query.limit) {
      const keys = Array.from(buckets.keys()).sort((a, b) => a - b)
      keys.slice(0, keys.length - query.limit).forEach(key => buckets.delete(key))
    }
    while (seen.size > Math.max(200, Math.min(20000, query.limit * 4))) seen.delete(seen.keys().next().value)
  }
  const bucketTime = timestamp => anchor === undefined ? null : anchor + Math.floor((timestamp - anchor) / duration) * duration
  return {
    seed (body, coverageTo = query.range.to, targetTime) {
      // 校验全部返回后才写入，错误响应不能污染已有图表。
      const entries = propertyRows(body).map(row => {
        if (typeof row.time !== 'string' || !/(?:Z|[+-]\d\d:?\d\d)$/.test(row.time)) throw new Error('实时聚合需要平台返回带时区的桶时间，请关闭接续或检查平台时间格式支持')
        const timestamp = Date.parse(row.time)
        if (!Number.isFinite(timestamp)) throw new Error('平台聚合桶时间无法解析')
        const count = numeric(row[extra.count]), sum = numeric(row[extra.sum])
        if (agg === 'AVG' && (count === null || count < 0 || !Number.isInteger(count) || (count > 0 && sum === null))) throw new Error('实时 AVG 缺少有效的 SUM/COUNT，不能以平均值代替累计量')
        return { timestamp, row: { ...row }, coverageTo, count: count || 0, sum: sum || 0,
          value: agg === 'AVG' ? (count > 0 ? sum / count : null) : numeric(row[query.property]) }
      })
      const selected = targetTime === undefined ? entries : entries.filter(item => item.timestamp <= targetTime).sort((a, b) => b.timestamp - a.timestamp).slice(0, 1)
      if (targetTime !== undefined && !selected.length) throw new Error('平台没有返回待校准的时间桶，请重试')
      // 日历校准查询两侧可能有不完整的邻桶，只替换目标桶，不能覆盖其余历史。
      selected.forEach(item => { buckets.set(item.timestamp, item); if (duration) anchor = item.timestamp })
      trim()
      if (targetTime !== undefined) {
        const next = entries.filter(item => item.timestamp > targetTime).sort((a, b) => a.timestamp - b.timestamp)[0]
        return { from: selected[0].timestamp, to: next ? Math.min(coverageTo, next.timestamp - 1) : coverageTo }
      }
    },
    append (sample) {
      if (!inLiveRange(sample.timestamp, range) || !hasValue(sample.value)) return 'ignored'
      const key = sample.messageId ? 'id:' + sample.messageId : 'ts:' + sample.timestamp
      const timestampKey = 'ts:' + sample.timestamp
      const signature = JSON.stringify([sample.timestamp, sample.value])
      if (seen.get(key) === signature || seen.get(timestampKey) === signature) return 'ignored'
      const corrected = seen.has(key) || seen.has(timestampKey)
      seen.set(key, signature); seen.set(timestampKey, signature); trim()
      const timestamp = duration ? bucketTime(sample.timestamp) : null
      const item = buckets.get(timestamp)
      if (corrected || timestamp === null || sample.timestamp <= (item ? item.coverageTo : query.range.to) || sample.timestamp < lastTimestamp) return 'reconcile'
      const value = numeric(sample.value)
      if (agg !== 'COUNT' && value === null) return 'ignored'
      const bucket = item || { timestamp, row: {}, count: 0, sum: 0, value: null, coverageTo: query.range.to }
      if (agg === 'AVG') { bucket.sum += value; bucket.count++; bucket.value = bucket.sum / bucket.count }
      if (agg === 'SUM') bucket.value = (bucket.value || 0) + value
      if (agg === 'COUNT') bucket.value = (bucket.value || 0) + 1
      if (agg === 'MAX') bucket.value = bucket.value === null ? value : Math.max(bucket.value, value)
      if (agg === 'MIN') bucket.value = bucket.value === null ? value : Math.min(bucket.value, value)
      buckets.set(timestamp, bucket); lastTimestamp = sample.timestamp; trim()
      return 'changed'
    },
    correction (sample, now = Date.now()) {
      const timestamp = duration ? bucketTime(sample.timestamp) : null
      // 日历桶交给平台分组；扩大两侧保证目标桶完整，最多只返回邻近几个桶。
      const from = Math.max(range.from, timestamp === null ? sample.timestamp - span * 2 : timestamp)
      const to = Math.min(range.to, Math.max(now, sample.timestamp), timestamp === null ? sample.timestamp + span * 2 : timestamp + duration - 1)
      return { ...query.data, query: { ...query.data.query, from, to, limit: timestamp === null ? 8 : 1 } }
    },
    correctionKey (sample) { return duration && anchor !== undefined ? bucketTime(sample.timestamp) : Math.floor(sample.timestamp / span) },
    rows () {
      return Array.from(buckets.values()).sort((a, b) => a.timestamp - b.timestamp).map(item => {
        const row = { ...item.row, timestamp: item.timestamp, time: timeLabel(item.timestamp, parameter.format), [query.property]: item.value }
        delete row[extra.sum]; delete row[extra.count]
        return row
      })
    }
  }
}
